import {
  Alert,
  AlertIcon,
  AlertDescription,
  HStack,
  VStack,
  Skeleton,
} from '@chakra-ui/react';
import React from 'react';

import {
  FundingRequestStatusEnumGQLType,
  FundingRequestTypeEnumGQLType,
} from 'types/dashboard-core-gqlTypes.generated';

import { FundingRequestErrorAlert } from '../shared/FundingRequestErrorAlert';
import { CheckAvailability } from './CheckAvailability';
import { CreateRequest, CreateRequestForMultiClass } from './CreateRequest';
import {
  canFundingRequestBeSubmitted,
  getFailedConstraintsMessages,
} from './CreateRequest/GlobalConstraints';
import { ReviewRequest, ReviewRequestMultiClass } from './ReviewRequest';
import { SubmitRequestControls } from './SubmitRequest';
import { useCreditFacilitiesOrThrow } from '~/contexts/CreditFacilitiesProvider';
import { colors } from '~/packages/common-frontend/colors';
import { Typography } from '~/packages/common-frontend/components/Typography';
import { useQueryParams } from '~/packages/common-frontend/hooks/useQueryParams';
import { useFundingRequestTemplateContextV1 } from '~/packages/funding-requests/hooks/useFundingRequestTemplateContextV1';
import { IdPill } from '~/packages/loans/ui/components/IdPill';

export const SubmitFundingRequest = ({
  fundingRequestId,
  status,
  type,
}: {
  fundingRequestId?: string;
  status?: FundingRequestStatusEnumGQLType;
  type: FundingRequestTypeEnumGQLType;
}) => {
  const {
    isLoading,
    errorMessage,
    context: { mode, allowedActions },
    template,
  } = useFundingRequestTemplateContextV1();

  if (isLoading) {
    return <Skeleton w="100%" h="600px" />;
  }

  if (errorMessage) {
    return <FundingRequestErrorAlert errorMessage={errorMessage} />;
  }

  // Use of resolved loan here is limited to retrieving facility details
  // Just get the first one if it exists
  const resolvedLoan = template && template.loans ? template.loans[0] : null;

  return (
    <VStack w="100%" justifyContent="flex-start" spacing="40px">
      <VStack
        w="100%"
        justifyContent="flex-start"
        p="40px"
        border={`1px solid ${colors.gray[200]}`}
        borderRadius="8px"
      >
        <HStack justifyContent="space-between" pb="1rem" w="100%">
          <Typography variant="section-title" fontWeight="bold" trim>
            Check {resolvedLoan?.facility?.creditFacilityName || 'Facility'}{' '}
            Availability
          </Typography>
          {resolvedLoan?.facility?.externalFacilityId && (
            <IdPill
              label="External Facility ID"
              value={resolvedLoan.facility.externalFacilityId}
            />
          )}
        </HStack>
        <CheckAvailability type={type} />
      </VStack>
      <VStack
        w="100%"
        alignItems="flex-start"
        p="40px"
        border={`1px solid ${colors.gray[200]}`}
        borderRadius="8px"
      >
        <Typography variant="section-title" fontWeight="bold">
          {allowedActions === 'mutable' ? 'Create Request' : 'Funding Request'}
        </Typography>
        <CreateRequest type={type} />
      </VStack>
      <VStack
        w="100%"
        alignItems="flex-start"
        p="40px"
        border={`1px solid ${colors.gray[200]}`}
        borderRadius="8px"
      >
        <Typography variant="section-title" fontWeight="bold">
          Review Request
        </Typography>
        <ReviewRequest status={status} fundingRequestId={fundingRequestId} />
      </VStack>
      <SubmitRequestControls
        canCancelOnPending={
          (mode === 'create' || mode === 'upsize') &&
          allowedActions === 'mutable'
        }
      />
    </VStack>
  );
};

/**
 * @jendevelops DO NOT UPDATE UNLESS YOU'RE TRYING TO UPDATE IN PROD FOR CUSTOMERS
 *
 * This component is used for prod customers that are using the multi class aspect of the funding request create route
 *
 * This is LIVE IN PROD for the following customers
 * - Tamara
 */

export const SubmitFundingRequestForAlphaCustomers = () => {
  const { facilities } = useCreditFacilitiesOrThrow();
  const params = useQueryParams();
  const creditFacilityId = params.get('creditFacilityId');

  const {
    isLoading,
    errorMessage,
    context: {
      data: { expectedSubmission },
    },
  } = useFundingRequestTemplateContextV1();

  const canSubmitFundingRequest = canFundingRequestBeSubmitted(
    expectedSubmission,
  );

  const fundingRequestFailedConstraintsMessages = getFailedConstraintsMessages([
    ...expectedSubmission.draw.global.constraints,
    ...expectedSubmission.recycle.global.constraints,
  ]);

  const creditFacility = facilities.find(
    (facility) => facility.id === creditFacilityId,
  );

  if (isLoading) {
    return <Skeleton w="100%" h="600px" />;
  }

  if (errorMessage) {
    return <FundingRequestErrorAlert errorMessage={errorMessage} />;
  }

  if (!canSubmitFundingRequest) {
    return (
      <>
        <Alert status="error" m="1rem 0">
          <AlertIcon />
          <AlertDescription>
            {`Unable to submit funding request due to failed constraint${
              fundingRequestFailedConstraintsMessages.length > 1 ? 's' : ''
            }: ${fundingRequestFailedConstraintsMessages.join(', ')}`}
          </AlertDescription>
        </Alert>
      </>
    );
  }

  return (
    <VStack w="100%" justifyContent="flex-start" spacing="40px">
      <VStack
        w="100%"
        justifyContent="flex-start"
        p="40px"
        border={`1px solid ${colors.gray[200]}`}
        borderRadius="8px"
      >
        <HStack w="100%" justifyContent="space-between">
          <Typography variant="section-title" fontWeight="bold">
            Check Facility Availability
          </Typography>
          {/* @jendevelops TODO: add availability badge */}
        </HStack>
        <CheckAvailability type="origination" />
      </VStack>
      <VStack
        w="100%"
        alignItems="flex-start"
        p="40px"
        border={`1px solid ${colors.gray[200]}`}
        borderRadius="8px"
      >
        <Typography variant="section-title" fontWeight="bold">{`Create Request${
          creditFacility ? ` For ${creditFacility.name}` : ''
        }`}</Typography>
        {/* @jendevelops TODO update create request for tamara specific one */}
        <CreateRequestForMultiClass />
      </VStack>
      <VStack
        w="100%"
        alignItems="flex-start"
        p="40px"
        border={`1px solid ${colors.gray[200]}`}
        borderRadius="8px"
      >
        <Typography variant="section-title" fontWeight="bold">
          Review Request
        </Typography>
        <ReviewRequestMultiClass />
      </VStack>
      <SubmitRequestControls />
    </VStack>
  );
};
